import { LitElement, html, css, nothing } from 'lit';
import '@brightspace-ui/core/components/inputs/input-text.js';
import '@brightspace-ui/core/components/button/button.js';
import '@brightspace-ui/core/components/icons/icon.js';

export class PMNonStandardSSOC2A extends LitElement {
  static properties = {
    environmentId: { type: String },
    tenantId: { type: String },
    client: { type: Object },
    ssoDomains: { type: Array },
    _newDomainInput: { state: true },
    isIgnored: { state: true },
    _animationClass: { state: true },
    _expanded: { state: true },
    environment: { state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .non-standard-banner {
      margin: 1rem 0;
      padding: 1rem;
      border-left: 4px solid var(--d2l-color-cinnabar);
      background-color: var(--d2l-color-regolith);
      display: flex;
      flex-direction: column;
      gap: 1rem;
      border-radius: 8px;
      overflow: hidden;

    }
    .header-container:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    .header-container {
      cursor: pointer;
      transition: background-color 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: space-between;
      user-select: none;
    }

    .c2a-header {
      font-size: 1.25rem;
      font-weight: bold;
    }

    .button-container {
      display: flex;
      gap: 0.5rem;
    }

    .domain-section {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.5rem;
      background-color: #f7f7f7;
      border-radius: 4px;
    }
    .domain-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .domain-label {
      font-weight: bold;
      min-width: 120px;
    }
    code {
      background-color: #eee;
      padding: 0.2rem 0.4rem;
      border-radius: 3px;
      font-family: monospace;
    }

    .fade-in {
      opacity: 1;
      transform: translateY(0);
      transition: opacity 0.5s ease, transform 0.5s ease;
    }
    .fade-out {
      opacity: 0;
      transform: translateY(-10px);
      transition: opacity 0.5s ease, transform 0.5s ease;
    }

    .content {
      animation: fadeIn 0.3s ease;
    }
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-5px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  constructor() {
    super();
    this._newDomainInput = '';
    this.ssoDomains = [];
    this.isIgnored = false;
    this._animationClass = 'fade-in';
    this._expanded = false;
  }

  async firstUpdated() {
    this.environment = await this.client.fetchEnvironment(this.environmentId);
  }

  async updated(changedProperties) {
    // When tenantId or environmentId change, check localStorage for an ignore flag.
    if (changedProperties.has('environmentId')) this.environment = await this.client.fetchEnvironment(this.environmentId);
    if (changedProperties.has('tenantId') || changedProperties.has('environmentId')) {
      if (this.tenantId && this.environmentId) {
        const key = `pmNonStandardSSOIgnored-${this.tenantId}-${this.environmentId}`;
        if (localStorage.getItem(key)) {
          this.isIgnored = true;
        }
      }
    }
  }

  render() {
    // If the user has ignored the CTA or if the configuration is standard, render nothing.
    if (this.isIgnored) return nothing;
    if (!this.environment?.status?.tags.includes('STANDARDIZE-SSO')) return nothing;
    if (!this._isNonStandardSso()) return nothing;

    const domain = this._newDomainInput.trim().toLowerCase();
    const samlDomain = domain ? `${domain}.${this.environmentId}.skillswave.dev` : '';
    const magicDomain = domain ? `${domain}-ml.${this.environmentId}.skillswave.dev` : '';

    return html`
      <div class="non-standard-banner d2l-alert d2l-alert-warning ${this._animationClass}">
        <div class="header-container" @click=${this._toggleExpand}>
          <div class="c2a-header">Non-standard SSO Detected</div>
          <d2l-icon icon=${this._expanded ? 'tier1:arrow-collapse' : 'tier1:arrow-expand'}></d2l-icon>
        </div>
        ${this._expanded ? html`
              <div class="content">
                <div class="d2l-alert-text">
                  <p>
                    This tenant has a non-standard SSO configuration.
                    Do you want to make the configuration standard?
                    <strong>This will delete all records</strong> and replace them with a standard SAML and MagicLink record.
                  </p>
                </div>
                <d2l-input-text
                  label="Enter domain to apply to new configuration"
                  .value=${this._newDomainInput}
                  @input=${this._onInputChange}>
                </d2l-input-text>
                ${domain ? html`
                      <div class="domain-section">
                        <div class="domain-row">
                          <div class="domain-label">SAML Domain:</div>
                          <code>${samlDomain}</code>
                        </div>
                        <div class="domain-row">
                          <div class="domain-label">Magic Link Domain:</div>
                          <code>${magicDomain}</code>
                        </div>
                      </div>
                    ` : nothing}
                <div class="button-container">
                  <d2l-button
                    primary
                    ?disabled=${!domain}
                    @click=${this._handleFixSSO}>
                    Fix
                  </d2l-button>
                  <d2l-button @click=${this._handleIgnore}>
                    Ignore
                  </d2l-button>
                </div>
              </div>
            ` : nothing}
      </div>
    `;
  }

  _toggleExpand() {
    this._expanded = !this._expanded;
  }

  _isNonStandardSso() {
    // If in production, do not show the banner.
    if (this.client && typeof this.client.isProd === 'function' && this.client.isProd()) {
      return false;
    }
    if (!Array.isArray(this.ssoDomains) || this.ssoDomains.length === 0) {
      return true;
    }
    let krakenSSORecord;
    let samlCount = 0;
    for (const sso of this.ssoDomains) {
      if (sso.loginType === 'saml') {
        samlCount++;
        if (
          sso.issuer === 'kraken' &&
          sso?.entryPoint?.toLowerCase().includes('kraken')
        ) {
          krakenSSORecord = sso;
        }
      }
    }
    return (!krakenSSORecord || samlCount !== 1 || this.ssoDomains.length > 2);
  }

  _onInputChange(e) {
    this._newDomainInput = e.target.value;
  }

  async _handleFixSSO(e) {
    // Prevent header toggle when clicking the Fix button.
    e.stopPropagation();
    if (!this._newDomainInput || !this.client) return;
    try {
      // Uncomment and implement the client call when ready:
      await this.client.standardizeTenantSSO(
        this.environmentId,
        this.tenantId,
        this._newDomainInput
      );
      this.client.createAlert('success', 'SSO configuration standardized!');
      this.dispatchEvent(new CustomEvent('sso-fixed', { bubbles: true, composed: true }));
    } catch (err) {
      this.client.createAlert('error', `Failed to fix SSO: ${err.message}`);
    }
  }

  _handleIgnore(e) {
    // Prevent header toggle when clicking the Ignore button.
    e.stopPropagation();
    // Trigger the fade-out animation.
    this._animationClass = 'fade-out';
    // After the transition duration, mark as ignored.
    setTimeout(() => {
      if (this.tenantId && this.environmentId) {
        const key = `pmNonStandardSSOIgnored-${this.tenantId}-${this.environmentId}`;
        localStorage.setItem(key, 'true');
      }
      this.isIgnored = true;
    }, 500); // Duration should match the CSS transition time.
  }
}

customElements.define('pm-non-standard-sso-c2a', PMNonStandardSSOC2A);
